import React, { useRef, useState } from "react";
import { Layout } from "../../components/layout/Layout";
import { PageHeader } from "../../components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { getCommunicationSummary } from "../../services/api/communicationLearning";
import { Link } from "react-router-dom";
import { ROUTES } from "../../utils/constants";
import { exportElementToPdf } from "../../utils/pdfExport";

export default function CommunicationAnalyticsPage() {
  const printRef = useRef<HTMLDivElement>(null);
  const [studentId, setStudentId] = useState("");
  const [summary, setSummary] = useState<Record<string, unknown> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!studentId.trim()) return;
    setLoading(true);
    setErr(null);
    try {
      const s = await getCommunicationSummary(studentId.trim());
      setSummary(s);
    } catch (e: unknown) {
      setSummary(null);
      setErr(e instanceof Error ? e.message : "Failed to load summary");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Communication analytics"
        subtitle="Class-level charts can be layered on this summary in a follow-up iteration."
      />
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <Card className="border border-purple-100 shadow-md bg-gradient-to-br from-white to-indigo-50/40">
          <CardHeader>
            <CardTitle className="text-base text-purple-900">Student summary (JSON)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2 items-end">
              <input
                className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-slate-200"
                placeholder="Student user id"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
              />
              <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
                {loading ? "Loading…" : "Load"}
              </Button>
            </div>
            {err && <p className="text-sm text-red-600">{err}</p>}
            {summary && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const el = printRef.current;
                    if (el) void exportElementToPdf(el, { filename: "communication-summary" });
                  }}
                >
                  Download PDF
                </Button>
                <div ref={printRef} className="p-4 bg-white text-slate-900 text-sm rounded-lg border border-slate-200">
                  <h3 className="font-bold text-lg mb-2">Communication summary</h3>
                  <pre className="text-xs whitespace-pre-wrap font-mono">{JSON.stringify(summary, null, 2)}</pre>
                </div>
              </>
            )}
            <p className="text-xs text-slate-500">
              Use{" "}
              <Link className="text-purple-700 underline" to={ROUTES.TEACHER_STUDENT_COMMUNICATION_PROGRESS}>
                Communication progress
              </Link>{" "}
              for per-turn review and highlights.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
